import { useEffect, useRef, useState } from 'react';
import { Globe, Upload, Loader2, Trash2, Film, Music, Image, X, Play } from 'lucide-react';
import toast from 'react-hot-toast';
import { mediaApi, crawlerApi } from '../../api/client';
import { resolveMediaUrl } from '../../lib/mediaUrl';
import { formatFileSize } from './storyUtils';
import ConfirmDialog from '../ConfirmDialog';

export default function MediaTab() {
  const [mediaAssets, setMediaAssets] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaDragOver, setMediaDragOver] = useState(false);
  const mediaFileRef = useRef(null);

  const [activeTab, setActiveTab] = useState('all');
  const [previewAsset, setPreviewAsset] = useState(null);
  const [confirmAssetId, setConfirmAssetId] = useState(null);

  const [crawlUrl, setCrawlUrl] = useState('');
  const [crawlLoading, setCrawlLoading] = useState(false);
  const [crawlResult, setCrawlResult] = useState(null);
  const [crawlError, setCrawlError] = useState(null);

  useEffect(() => { loadMedia(activeTab); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadMedia(tab) {
    setMediaLoading(true);
    try {
      const params = tab && tab !== 'all' ? { type: tab } : {};
      const result = await mediaApi.list(params);
      const raw = result?.items ?? result ?? [];
      setMediaAssets(raw.map(m => ({ ...m, id: m.id || m._id })));
    } catch { toast.error('Failed to load media'); }
    finally { setMediaLoading(false); }
  }

  async function handleMediaUpload(files) {
    setMediaUploading(true);
    try {
      for (const file of Array.from(files)) {
        const raw = await mediaApi.upload(file);
        const asset = { ...raw, id: raw.id || raw._id };
        setMediaAssets(prev => [
          ...(activeTab === 'all' || asset.type === activeTab ? [asset] : []),
          ...prev,
        ]);
      }
      toast.success(`${files.length} file${files.length > 1 ? 's' : ''} uploaded`);
    } catch { toast.error('Upload failed'); }
    finally { setMediaUploading(false); }
  }

  function deleteMediaAsset(id, e) {
    e.stopPropagation();
    setConfirmAssetId(id);
  }

  async function doDeleteMedia() {
    try {
      await mediaApi.delete(confirmAssetId);
      setMediaAssets(prev => prev.filter(m => m.id !== confirmAssetId));
      toast.success('Media deleted');
    } catch { toast.error('Failed to delete media'); }
    finally { setConfirmAssetId(null); }
  }

  async function handleCrawlWebsite(e) {
    e.preventDefault();
    const trimmed = crawlUrl.trim();
    if (!trimmed) return;
    setCrawlLoading(true); setCrawlError(null); setCrawlResult(null);
    try {
      const data = await crawlerApi.extract(trimmed);
      setCrawlResult(data);
    } catch (err) {
      setCrawlError(err instanceof Error ? err.message : 'Crawl failed');
    } finally { setCrawlLoading(false); }
  }

  const visibleAssets = mediaAssets;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-1" style={{ color: 'var(--t-text)' }}>Media Library</h1>
        <p className="text-sm" style={{ color: 'var(--t-muted)' }}>Upload and manage your images, videos, and audio files.</p>
      </div>

      {/* Crawl Website */}
      <div className="mb-8 rounded-xl p-6" style={{ background: 'var(--t-surface)', border: '1px solid var(--t-border)' }}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--brand-primary)10', color: 'var(--brand-primary)' }}>
            <Globe size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--t-text)' }}>Import from Website</h3>
            <p className="text-xs" style={{ color: 'var(--t-muted)' }}>Extract images, logos, and brand colors</p>
          </div>
        </div>
        <form onSubmit={handleCrawlWebsite} className="flex gap-2 mb-3">
          <input
            type="url" value={crawlUrl} onChange={e => setCrawlUrl(e.target.value)}
            placeholder="https://example.com" className="flex-1 px-3 py-2 text-sm rounded-lg"
            style={{ background: 'var(--t-panel)', border: '1px solid var(--t-border)', color: 'var(--t-text)' }}
            required />
          <button type="submit" disabled={crawlLoading || !crawlUrl.trim()}
            className="px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-40"
            style={{ background: 'var(--brand-primary)', color: '#fff' }}>
            {crawlLoading ? <Loader2 size={14} className="animate-spin" /> : 'Crawl'}
          </button>
        </form>

        {crawlLoading && (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={20} className="animate-spin text-indigo-400" />
          </div>
        )}

        {crawlError && (
          <div className="p-3 rounded-lg text-xs" style={{ background: '#ef444410', border: '1px solid #ef444440', color: '#f87171' }}>
            {crawlError}
          </div>
        )}

        {crawlResult && !crawlLoading && (
          <div className="space-y-4">
            {crawlResult.logo && (
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--t-muted)' }}>Logo</p>
                <div className="flex items-center gap-3 p-3 rounded-lg" style={{ background: 'var(--t-panel)' }}>
                  <img src={crawlResult.logo} alt="Logo" className="h-10 w-auto object-contain rounded" style={{ maxWidth: '100px' }} />
                  <button onClick={() => { navigator.clipboard.writeText(crawlResult.logo); toast.success('Logo URL copied!'); }}
                    className="px-3 py-1 text-xs font-medium rounded transition-colors ml-auto"
                    style={{ background: 'var(--brand-primary)', color: '#fff' }}>
                    Copy URL
                  </button>
                </div>
              </div>
            )}

            {crawlResult.colors.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--t-muted)' }}>Brand Colors</p>
                <div className="flex flex-wrap gap-2">
                  {crawlResult.colors.map((color, i) => (
                    <button key={i}
                      onClick={() => { navigator.clipboard.writeText(color); toast.success('Color copied!'); }}
                      className="w-10 h-10 rounded-lg border transition-transform hover:scale-110 relative group"
                      style={{ background: color, borderColor: 'var(--t-border)' }}
                      title={`${color} — click to copy`}>
                      <span className="absolute -bottom-5 left-1/2 -translate-x-1/2 text-[10px] opacity-0 group-hover:opacity-100 whitespace-nowrap"
                        style={{ color: 'var(--t-muted)' }}>{color}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {crawlResult.images.length > 0 && (
              <div>
                <p className="text-xs font-semibold mb-2" style={{ color: 'var(--t-muted)' }}>Images ({crawlResult.images.length})</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
                  {crawlResult.images.map((image, i) => (
                    <div key={i} className="group relative rounded-xl overflow-hidden"
                      style={{ aspectRatio: '1', background: 'var(--t-panel)', border: '1px solid var(--t-border)' }}>
                      <img src={image.url} alt={image.alt} className="w-full h-full object-cover" loading="lazy" />
                      <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-[10px] font-semibold capitalize"
                        style={{
                          background: image.type === 'hero' ? 'rgba(99,102,241,0.85)' : image.type === 'product' ? 'rgba(236,72,153,0.85)' : image.type === 'logo' ? 'rgba(16,185,129,0.85)' : 'rgba(245,158,11,0.85)',
                          color: '#fff', backdropFilter: 'blur(4px)',
                        }}>{image.type}</div>
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                        style={{ background: 'rgba(0,0,0,0.55)' }}>
                        <button onClick={() => { navigator.clipboard.writeText(image.url); toast.success('Image URL copied!'); }}
                          className="px-3 py-1.5 text-xs font-medium rounded-lg" style={{ background: 'var(--brand-primary)', color: '#fff' }}>
                          Copy URL
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Zone */}
      <div
        className="mb-8 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition-all"
        style={{ borderColor: mediaDragOver ? 'var(--brand-primary)' : 'var(--t-border2)', background: mediaDragOver ? 'var(--brand-primary)10' : 'var(--t-surface)' }}
        onDragOver={e => { e.preventDefault(); setMediaDragOver(true); }}
        onDragLeave={() => setMediaDragOver(false)}
        onDrop={e => { e.preventDefault(); setMediaDragOver(false); if (e.dataTransfer.files.length) handleMediaUpload(e.dataTransfer.files); }}
        onClick={() => mediaFileRef.current?.click()}
      >
        <input ref={mediaFileRef} type="file" multiple accept="image/*,video/*,audio/*" className="hidden"
          onChange={e => { if (e.target.files?.length) handleMediaUpload(e.target.files); e.target.value = ''; }} />
        {mediaUploading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--brand-primary)' }} />
            <p className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>Uploading...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: 'var(--brand-primary)10', color: 'var(--brand-primary)' }}>
              <Upload size={24} />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--t-text)' }}>Drop files here or click to upload</p>
              <p className="text-xs mt-1" style={{ color: 'var(--t-muted)' }}>Supports images, videos, and audio files</p>
            </div>
          </div>
        )}
      </div>

      {/* Type Tabs */}
      <div className="flex gap-1 mb-5 border-b" style={{ borderColor: 'var(--t-border)' }}>
        {[
          { key: 'all',   label: 'All' },
          { key: 'image', label: 'Images' },
          { key: 'video', label: 'Videos' },
          { key: 'audio', label: 'Audio' },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className="px-4 py-2.5 text-sm font-medium transition-colors"
            style={{
              color: activeTab === key ? 'var(--brand-primary)' : 'var(--t-muted)',
              background: 'transparent',
              border: 'none',
              borderBottom: activeTab === key ? '2px solid var(--brand-primary)' : '2px solid transparent',
              marginBottom: -1,
              cursor: 'pointer',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Media Grid */}
      {mediaLoading ? (
        <div className="flex items-center justify-center py-24">
          <div className="text-center">
            <div className="w-9 h-9 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm" style={{ color: 'var(--t-muted)' }}>Loading media...</p>
          </div>
        </div>
      ) : visibleAssets.length === 0 ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-center max-w-sm">
            <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-5" style={{ background: 'var(--t-panel)' }}>
              <Image size={32} style={{ color: 'var(--t-muted)' }} />
            </div>
            <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--t-text)' }}>{mediaAssets.length === 0 ? 'No media files yet' : `No ${activeTab} files`}</h2>
            <p className="text-sm" style={{ color: 'var(--t-muted)' }}>{mediaAssets.length === 0 ? 'Upload images, videos, or audio files to use in your stories.' : 'Upload files or switch to another tab.'}</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {visibleAssets.map(asset => (
            <div key={asset.id} className="group relative rounded-xl overflow-hidden transition-all duration-200 cursor-pointer"
              style={{ aspectRatio: '1', background: 'var(--t-panel)', border: '1px solid var(--t-border)', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}
              onClick={() => setPreviewAsset(asset)}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.14)'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'var(--brand-primary)'; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = 'var(--t-border)'; }}
            >
              {asset.type === 'image' ? (
                <img src={resolveMediaUrl(asset.thumbnailUrl || asset.url)} alt={asset.altText || asset.originalFilename} className="w-full h-full object-cover" />
              ) : asset.type === 'video' ? (
                <div className="w-full h-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
                  {asset.posterUrl ? <img src={resolveMediaUrl(asset.posterUrl)} alt="" className="w-full h-full object-cover" /> : <Film size={28} className="text-white/40" />}
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.85)', backdropFilter: 'blur(4px)' }}>
                      <Play size={16} className="text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-2" style={{ background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)' }}>
                  <Music size={28} className="text-white/40" />
                  <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.85)', backdropFilter: 'blur(4px)' }}>
                      <Play size={16} className="text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                </div>
              )}

              <div className="absolute top-2 left-2 px-1.5 py-0.5 rounded text-xs font-semibold capitalize"
                style={{
                  background: asset.type === 'image' ? 'rgba(99,102,241,0.8)' : asset.type === 'video' ? 'rgba(236,72,153,0.8)' : 'rgba(16,185,129,0.8)',
                  color: '#fff', backdropFilter: 'blur(4px)',
                }}>{asset.type}</div>

              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-2"
                style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.7) 100%)' }}>
                <div className="flex justify-end">
                  <button onClick={e => deleteMediaAsset(asset.id, e)}
                    className="w-7 h-7 bg-red-500/60 hover:bg-red-500/80 rounded-md flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Delete">
                    <Trash2 size={11} className="text-white" />
                  </button>
                </div>
                <div>
                  <p className="text-white text-xs font-semibold truncate">{asset.originalFilename}</p>
                  <p className="text-white/60 text-xs mt-0.5">{formatFileSize(asset.size)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {previewAsset && (
        <MediaPreviewModal asset={previewAsset} onClose={() => setPreviewAsset(null)} />
      )}

      <ConfirmDialog
        isOpen={confirmAssetId !== null}
        onClose={() => setConfirmAssetId(null)}
        onConfirm={doDeleteMedia}
        title="Delete media file?"
        message="This file will be permanently deleted from your library."
        confirmLabel="Delete"
      />
    </div>
  );
}

function MediaPreviewModal({ asset, onClose }) {
  const url = resolveMediaUrl(asset.url);

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative flex flex-col rounded-2xl overflow-hidden"
        style={{
          background: 'var(--t-bg)',
          border: '1px solid var(--t-border)',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          maxWidth: asset.type === 'audio' ? '420px' : '90vw',
          width: '100%',
          maxHeight: '90vh',
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 flex-shrink-0" style={{ borderBottom: '1px solid var(--t-border)' }}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="px-1.5 py-0.5 rounded text-[10px] font-bold capitalize"
              style={{
                background: asset.type === 'image' ? 'rgba(99,102,241,0.15)' : asset.type === 'video' ? 'rgba(236,72,153,0.15)' : 'rgba(16,185,129,0.15)',
                color: asset.type === 'image' ? 'var(--brand-400)' : asset.type === 'video' ? '#f472b6' : '#34d399',
              }}>
              {asset.type}
            </span>
            <p className="text-sm font-medium truncate" style={{ color: 'var(--t-text)' }}>{asset.originalFilename}</p>
          </div>
          <button
            onClick={onClose}
            className="ml-3 w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-opacity hover:opacity-60"
            style={{ background: 'var(--t-panel)' }}
          >
            <X size={14} style={{ color: 'var(--t-muted)' }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto flex items-center justify-center" style={{ minHeight: 0 }}>
          {asset.type === 'image' && (
            <img
              src={url}
              alt={asset.originalFilename}
              style={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain', display: 'block' }}
            />
          )}

          {asset.type === 'video' && (
            <video
              src={url}
              controls
              autoPlay
              style={{ maxWidth: '100%', maxHeight: '75vh', display: 'block', background: '#000' }}
            />
          )}

          {asset.type === 'audio' && (
            <div className="flex flex-col items-center gap-5 px-8 py-10 w-full">
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center"
                style={{ background: 'linear-gradient(135deg, #2d1b69 0%, #11998e 100%)' }}>
                <Music size={36} className="text-white/70" />
              </div>
              <p className="text-sm font-medium text-center" style={{ color: 'var(--t-text)' }}>{asset.originalFilename}</p>
              <audio src={url} controls autoPlay className="w-full" style={{ colorScheme: 'dark' }} />
            </div>
          )}
        </div>

        {/* Footer */}
        {asset.size && (
          <div className="px-4 py-2 flex-shrink-0 text-right" style={{ borderTop: '1px solid var(--t-border)' }}>
            <span className="text-[11px]" style={{ color: 'var(--t-muted)' }}>{formatFileSize(asset.size)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
